import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * @interface BeforeInstallPromptEvent
 * @description Extensión del evento nativo para incluir métodos específicos del prompt de instalación PWA.
 * @property {() => Promise<void>} prompt - Función que muestra el prompt de instalación.
 * @property {Promise<{outcome: 'accepted' | 'dismissed'}>} userChoice - Promesa que resuelve con la elección del usuario.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * @component InstallPrompt
 * @description Componente que maneja el prompt de instalación para aplicaciones web progresivas (PWA).
 * Detecta cuando la aplicación puede ser instalada y muestra un banner promocional para animar al usuario a instalarla.
 * También maneja el caso especial de iOS que no soporta el evento beforeinstallprompt nativo.
 * @returns {React.ReactElement | null} El componente renderizado con el banner de instalación o null si no debe mostrarse.
 */
export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandaloneMode =
      'standalone' in window.navigator &&
      (window.navigator as typeof navigator & { standalone?: boolean }).standalone;

    if (isIOS && !isInStandaloneMode) {
      setShowPrompt(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  const dismissedTime = localStorage.getItem('pwa-install-dismissed');
  if (dismissedTime) {
    const daysSinceDismissed = (Date.now() - parseInt(dismissedTime, 10)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissed < 7) return null;
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:w-[min(100%,26rem)] motion-safe:animate-slide-up"
      role="dialog"
      aria-modal="false"
      aria-labelledby="pwa-install-title"
    >
      <div className="group relative overflow-hidden rounded-2xl border border-host-200/90 bg-background-primary shadow-lg ring-1 ring-host-200/30 transition-[box-shadow,transform,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-host-300/90 hover:shadow-xl">
        <div
          className="pointer-events-none h-1.5 bg-linear-to-r from-host-400 via-host-300 to-host-400"
          aria-hidden
        />

        <div className="p-4 sm:p-5">
          <div className="flex gap-3 sm:gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-host-100/90 text-host-600 ring-1 ring-host-200/70 shadow-sm transition-[transform,background-color] duration-200 ease-out group-hover:scale-105 group-hover:bg-host-100">
              <FontAwesomeIcon icon={faDownload} className="text-lg" aria-hidden />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3
                  id="pwa-install-title"
                  className="text-base font-bold tracking-tight text-gray-900 sm:text-lg"
                >
                  Instalar Rfacil Materialidad
                </h3>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="-mr-1 -mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-host-50 hover:text-host-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-host-400 focus-visible:ring-offset-2"
                  aria-label="Cerrar"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
              </div>

              {isIOS ? (
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                  Toca el botón <span className="font-semibold text-gray-800">Compartir</span> y luego{' '}
                  <span className="font-semibold text-gray-800">Agregar a pantalla de inicio</span>.
                </p>
              ) : (
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                  Instala la aplicación para acceso rápido y uso sin conexión, con la misma experiencia que en el
                  navegador.
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={handleDismiss}
              className="order-2 w-full rounded-xl border border-gray-200 bg-background-primary px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:shadow sm:order-1 sm:w-auto"
            >
              Ahora no
            </button>
            {!isIOS && deferredPrompt ? (
              <button
                type="button"
                onClick={handleInstallClick}
                className="order-1 w-full rounded-xl bg-linear-to-r from-host-500 to-host-600 px-4 py-2.5 text-sm font-semibold text-text-secondary shadow-md transition-all duration-200 hover:from-host-600 hover:to-host-700 hover:shadow-lg sm:order-2 sm:w-auto"
              >
                Instalar ahora
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
